import { useEffect, useMemo, useState } from "react";

import type { OtPurposesByCategoryAndUser } from "@jti/fe-common";
import { BrowserStorage } from "@jti/rest-api";

type StorageConsentData = {
  popUpShownDates: Date[];
};

const storageKey = "consentData";

export type ConsentReminderType = "offer_consent" | "product_consent" | "ploom_club_consent" | null;
export type EligibleConsentReminderType = Exclude<ConsentReminderType, null>;

export type ConsentReminderConfig = {
  isEnabled: boolean;
  maximumOccurrencesPerYear: number;
  consentExpirationLimit: number;
  popUpDelay: number;
  offerConsentName?: string;
  productInformationConsentName?: string;
  ploomClubConsentName?: string;
};

type ConsentEligibilityConfig = Pick<
  ConsentReminderConfig,
  "consentExpirationLimit" | "offerConsentName" | "productInformationConsentName" | "ploomClubConsentName"
>;

type UseConsentReminderProps = {
  config: ConsentReminderConfig;
  consents?: OtPurposesByCategoryAndUser[];
  isLoading: boolean;
  isError: boolean;
};

const DAY_IN_MS = 1000 * 3600 * 24;

const getShownDates = (browserStorage: BrowserStorage) => {
  try {
    const consentData: StorageConsentData = browserStorage.get(storageKey, { popUpShownDates: [] });
    return Array.isArray(consentData.popUpShownDates) ? consentData.popUpShownDates.map((date) => new Date(date)) : [];
  } catch {
    return [];
  }
};

const isConsentExpiringSoon = (consent: OtPurposesByCategoryAndUser, consentExpirationLimit: number) => {
  if (!consent.revokes_at) return true;

  const expirationDate = new Date(consent.revokes_at);
  const diffInDays = Math.floor((expirationDate.getTime() - Date.now()) / DAY_IN_MS);
  return diffInDays < consentExpirationLimit;
};

export const getConsentReminderTypes = (
  consents: OtPurposesByCategoryAndUser[],
  {
    consentExpirationLimit,
    offerConsentName,
    productInformationConsentName,
    ploomClubConsentName,
  }: ConsentEligibilityConfig
): EligibleConsentReminderType[] => {
  const offerConsent = consents.find((consent) => consent.name === offerConsentName);
  const productConsent = consents.find((consent) => consent.name === productInformationConsentName);
  const ploomClubConsent = consents.find((consent) => consent.name === ploomClubConsentName);

  return [
    offerConsent && !offerConsent.one_off && isConsentExpiringSoon(offerConsent, consentExpirationLimit)
      ? "offer_consent"
      : null,
    productConsent && !productConsent.one_off && isConsentExpiringSoon(productConsent, consentExpirationLimit)
      ? "product_consent"
      : null,
    ploomClubConsent && !ploomClubConsent.accepted ? "ploom_club_consent" : null,
  ].filter((consentType): consentType is EligibleConsentReminderType => consentType !== null);
};

export const getConsentReminderType = (
  consents: OtPurposesByCategoryAndUser[],
  config: ConsentReminderConfig
): ConsentReminderType => getConsentReminderTypes(consents, config)[0] ?? null;

export const useConsentReminder = ({ config, consents, isLoading, isError }: UseConsentReminderProps) => {
  const browserStorage = useMemo(() => new BrowserStorage(), []);

  const [isEligible, setIsEligible] = useState(false);
  const [isOpened, setIsOpened] = useState(true);
  const [consentTypes, setConsentTypes] = useState<EligibleConsentReminderType[]>([]);
  const {
    isEnabled,
    maximumOccurrencesPerYear,
    consentExpirationLimit,
    popUpDelay,
    offerConsentName,
    productInformationConsentName,
    ploomClubConsentName,
  } = config;

  useEffect(() => {
    if (!isEnabled || !(maximumOccurrencesPerYear > 0) || isLoading || isError || isEligible || !consents) {
      return;
    }

    const eligibleTypes = getConsentReminderTypes(consents, {
      consentExpirationLimit,
      offerConsentName,
      productInformationConsentName,
      ploomClubConsentName,
    });
    if (!eligibleTypes.length) return;

    const timeoutId = setTimeout(
      () => {
        const shownDates = getShownDates(browserStorage);
        const showsInLastYear = shownDates.filter((date) => Date.now() - date.getTime() < 365 * DAY_IN_MS).length;

        if (showsInLastYear >= maximumOccurrencesPerYear) return;

        setConsentTypes(eligibleTypes);
        setIsEligible(true);
        browserStorage.set(storageKey, { popUpShownDates: [...shownDates, new Date()] });
      },
      (popUpDelay ?? 0) * 1000
    );

    return () => clearTimeout(timeoutId);
  }, [
    browserStorage,
    consents,
    consentExpirationLimit,
    isEnabled,
    maximumOccurrencesPerYear,
    offerConsentName,
    popUpDelay,
    ploomClubConsentName,
    productInformationConsentName,
    isEligible,
    isError,
    isLoading,
  ]);

  return {
    isConsentReminderVisible: isEnabled && isEligible && isOpened,
    consentTypes,
    consentType: consentTypes.at(0) ?? null,
    closeConsentReminder: () => setIsOpened(false),
  };
};
